import { useSelector } from 'react-redux';

import { update } from 'core/actions/admin/data';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import Button from 'app/components/Button/base';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { selectDomain } from 'core/selectors/product/domain';
import { OrganisationStatus } from 'types/domain/organisation';
import { closestCenter, DndContext, KeyboardSensor, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CourseNodeModule } from 'app/pages/Create/Courses/Course/Modules/module';
import { Search } from 'app/components/Search';
import { SearchType } from 'types/domain/search';
import AddButton from 'app/components/Button/AddButton';
import { Modals, setModal } from 'core/slices/modals';
import { EntityTypes } from 'constants/entities';
import { ICourseDAO, ICourseOut, IModuleCourseNode } from 'types/domain/course';
import { selectCanEdit } from 'core/selectors/admin/coursePermission';
import { reduceArrayById } from 'utils/reduceArrayById';
import { IKeyArray } from 'types/IKeyArray';
import { selectCourse, selectIsDefaultCourse } from 'core/selectors/admin/course';
import { selectModuleCourseNodes } from 'core/selectors/admin/courseNodes';
import { Handles } from 'constants/handles';
 
export function Dashboard() {
  const dispatch = useAppDispatch();
  const domain = useSelector(selectDomain);
  const organisationStatus: OrganisationStatus = useAppSelector(state => state.session.organisationStatus);
   

  const courseId = useAppSelector(state => state.state[EntityTypes.COURSE]);
 
  const course: ICourseDAO = useAppSelector(selectCourse);

  const courseNodes: IModuleCourseNode[] = useAppSelector(selectModuleCourseNodes);
  const moduleNodes = useMemo(() => courseNodes, [courseNodes]);
  const courseNodesById: IKeyArray<IModuleCourseNode> = useMemo(() => reduceArrayById(courseNodes), [courseNodes]);
  const isDefault = useAppSelector(selectIsDefaultCourse);

  const courseOuts: IKeyArray<ICourseOut> = useAppSelector(state =>
    reduceArrayById(
      Object.values(state.entity.present[EntityTypes.COURSE_OUT]).filter(
        (ou: ICourseOut) => ou.courseId === courseId && ou.sourceHandle === Handles.DEFAULT,
      ),
      'sourceId',
    ),
  );
  const canEdit: boolean = useAppSelector(selectCanEdit);

  const [items, setItems] = useState<number[]>([]);

  useEffect(() => {
    setItems(moduleNodes.map((cn: any) => cn.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseNodes]);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const setChild = useCallback(
    (courseNodeId: number, parentId: number, childId: number) => {
      const node: IModuleCourseNode = courseNodesById[courseNodeId];
      const parent: IModuleCourseNode = courseNodesById[parentId];
      const oldParent: IModuleCourseNode = courseNodes[courseNodes.findIndex(a => a.id === courseNodeId) - 1];

      dispatch(
        update({
          type: EntityTypes.COURSE_NODE,
          parentEntityType: EntityTypes.COURSE_NODE,
          parentEntityId: parentId,
          parentEntityAttribute: !parent || parent.childId ? 'childId' : null,
          entity: { id: node.id, childId: node.childId ? childId : null },
          //persist: false,
        }),
      );

      if (oldParent) {
        if (oldParent.childId)
          dispatch(
            update({
              type: EntityTypes.COURSE_NODE,
              parentEntityType: EntityTypes.COURSE_NODE,
              parentEntityId: parentId,
              entity: { id: oldParent.id, childId: node.childId },
              //persist: false,
            }),
          );
        if (courseOuts[oldParent.id])
          dispatch(
            update({
              type: EntityTypes.COURSE_OUT,
              parentEntityType: EntityTypes.COURSE,
              parentEntityId: course.id,
              entity: { id: courseOuts[oldParent.id].id, targetId: node.childId || courseOuts[node.id]?.targetId },
              //persist: false,
            }),
          );
      }

      if (courseOuts[parentId]) {
        dispatch(
          update({
            type: EntityTypes.COURSE_OUT,
            parentEntityType: EntityTypes.COURSE,
            parentEntityId: course.id,
            entity: { id: courseOuts[parentId].id, targetId: node.id },
            //persist: false,
          }),
        );
      }
      if (courseOuts[node.id]) {
        dispatch(
          update({
            type: EntityTypes.COURSE_OUT,
            parentEntityType: EntityTypes.COURSE,
            parentEntityId: course.id,
            entity: { id: courseOuts[node.id].id, targetId: childId },
            //persist: false,
          }),
        );
      }
    },
    [course?.id, courseNodes, courseNodesById, courseOuts, dispatch],
  );

  const handleDragEnd = useCallback(
    event => {
      const { active, over } = event;

      if (active.id !== over.id) {
        const newPosition = courseNodes.findIndex(cn => cn.id === over.id);
        setItems(items => {
          const oldIndex = items.indexOf(active.id);
          const newIndex = items.indexOf(over.id);

          return arrayMove(items, oldIndex, newIndex);
        });

        setChild(active.id, newPosition === 0 ? course.courseNodeId : courseNodes[newPosition - 1]?.id, over.id);
      }
    },
    [course?.courseNodeId, courseNodes, setChild],
  );


 
  return (
    <>
      <div className="w-full h-full">
        <div className="mx-auto max-w-screen-2xl mt-10 sm:px-10 px-5 text-gray-900">
          <h2 className="howio-font-md text-2xl">Sandbox: Turn your content into high-impact training modules</h2>
          <div className="text-gray-500 text-md pt-5">Curious what you can create with How.io? Check out the sample modules below — they're just a taste of what's possible. Ready to dive in? Click <b>New Module</b> and turn your PDFs, videos, 3D models (and more) into awesome, unforgettable training.</div>
         
          <div className="pt-10 pb-5 flex-col border-b border-slate-200">
              <div className="flex justify-between ">
                <Search
                  className={'bg-white text-gray-800 mr-2 w-full'}
                  showResults={true}
                  inputClassName="w-full"
                  showSort={false}
                  placeholder="Search Modules"
                  searchType={SearchType.MODULE}
                />

                {canEdit && (
                  
                    <AddButton  
                      onClick={e => dispatch(setModal({ type: Modals.ADD_MODULE, id: null }))}
                      label="New Module"
                    />
                   
                )}
              </div>
            </div>
           
            <div className="w-full">
              <div className="grid sm:grid-cols-3 grid-cols-1 gap-6">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={courseNodes}>
                    {items.map((id, i) => (
                      <CourseNodeModule key={i} id={id} className={''} />
                    ))}
                  </SortableContext>
                </DndContext>
              </div> 
            </div>  
        </div>  
      </div>
    </>
  );
}
